

import { DATA_DISPLAY_QUERY_KEY, dataDisplay } from "@/api/dataDisplay";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { ActionHeader } from "@/features/standard/ActionHeader";
import { Header } from "@/features/standard/Header";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import useQueryString from "@/hooks/useQueryString";
import styled from "@emotion/styled";
import { Button, Flex, Pagination, Text } from "@mantine/core";
import { AcLoaderListGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import type { Column } from "react-data-grid";
import DataGrid from "react-data-grid";

const DataDisplayWrap = {
    Header: Header,
    HeaderTitle: HeaderTitle,
    HeaderSubTitle: HeaderSubTitle,
    ActionHeader: ActionHeader,
    ActionButton: Button,
    Wrapper: Wrapper,
};

export const DataDisplayPage = () => {

    const queryClient = useQueryClient();
    
    const rowHeight = 48;
    const headerRowHeight = 52;

    const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);
    const [activePage, setPage] = useState(initialQueryString.page);
    const { data: DisplayData } = useQuery(dataDisplay.get({
        page: activePage,
        pageSize: 10
    }));

    const rows = DisplayData?.data.rows ?? []


    const totalPage = DisplayData?.data.totalPages ?? 0
    const gridHeight = headerRowHeight + Math.max(rows.length, 1) * rowHeight;
    const columns: readonly Column<AcLoaderListGet200ResponseRowsInner>[] = [
        {
            key: "id",
            name: "순번",
            sortable: true,
            resizable: true,
        },
        {
            key: "manufactureCompany",
            name: "측정 일시",
            sortable: true,
            resizable: true,
            formatter: (formatterProps : { row: AcLoaderListGet200ResponseRowsInner }) => {
                return <>{dayjs(formatterProps.row.createdAt).format("YYYY-MM-DD HH:mm:ss")}</>
            }
        },
        {
            key: "voltage",
            name: "전압(V)",
            sortable: true,
            resizable: true,
        },
        {
            key: "current",
            name: "전류(A)",
            sortable: true,
            resizable: true,
        },
        {
            key: "power",
            name: "전력(W)",
            sortable: true,
            resizable: true,
        },
    ];

    useEffect(() => {
        const intervalId = setInterval(() => {
            queryClient.invalidateQueries([DATA_DISPLAY_QUERY_KEY]);
        }, 5000); // 5 초

        return () => clearInterval(intervalId);
    }, [queryClient]);


    return (
        <DataDisplayWrap.Wrapper>
            <DataDisplayWrap.Header>
                <DataDisplayWrap.HeaderTitle>데이터 표출</DataDisplayWrap.HeaderTitle>
                <DataDisplayWrap.HeaderSubTitle>
                    {"우리 회사에서 다루는 데이터표출에 대해 조회할 수 있는 페이지 입니다."}
                </DataDisplayWrap.HeaderSubTitle>
            </DataDisplayWrap.Header>
            <Flex w="100%" justify="space-between">
                <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
                </Flex>
            </Flex>
            <ContentSection>
                <TableWrapper>
                    <TableToolbar>
                        <Text fw={700} size="lg" c="#213547">
                            측정 데이터 목록
                        </Text>
                    </TableToolbar>
                    <DataGrid
                        columns={columns ?? []}
                        rows={rows}
                        rowHeight={rowHeight}
                        headerRowHeight={headerRowHeight}
                        style={{ height: gridHeight }}
                    />
                </TableWrapper>
                <PaginationWrapper>
                    <Pagination
                        onChange={setPage}
                        value={activePage}
                        total={totalPage}
                        size="lg"
                        radius="sm"
                        position="center"
                    />
                </PaginationWrapper>
            </ContentSection>
        </DataDisplayWrap.Wrapper>
    )
}

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 18px 0 8px;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  border: 1px solid #d7e0ea;
  border-radius: 20px;
  background:
    radial-gradient(circle at top right, rgba(107, 168, 255, 0.12), transparent 28%),
    linear-gradient(180deg, #ffffff 0%, #f6f9fc 100%);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);

  .rdg {
    border: none;
    background: transparent;
    color: #243447;
    font-size: 14px;
    --rdg-background-color: transparent;
    --rdg-header-background-color: rgba(241, 245, 249, 0.92);
    --rdg-row-hover-background-color: rgba(224, 242, 254, 0.55);
    --rdg-selection-color: #2b7fff;
    --rdg-border-color: #e5edf5;
  }

  .rdg-header-row {
    font-weight: 700;
    color: #334155;
    border-bottom: 1px solid #dbe4ee;
    backdrop-filter: blur(8px);
  }

  .rdg-cell,
  .rdg-header-cell {
    padding: 0 18px;
    border-inline-end: 1px solid #edf2f7;
  }

  .rdg-row {
    background-color: rgba(255, 255, 255, 0.88);
    transition: background-color 0.18s ease, transform 0.18s ease;
  }

  .rdg-row:nth-of-type(even) {
    background-color: rgba(248, 250, 252, 0.96);
  }

  .rdg-row:hover {
    background-color: rgba(239, 246, 255, 0.92);
  }

  .rdg-row[aria-selected="true"] {
    background-color: rgba(219, 234, 254, 0.85);
  }
`;

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e6edf5;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
`;

const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;

const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
